import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { AuthContext } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import ApplicationModal from '../components/ApplicationModal';
import { getProjectApplications } from '../services/applicationService';
import { getProjectById } from '../services/projectService';

const ProjectApplications = () => {
  const { projectId } = useParams();
  const { currentUser } = useContext(AuthContext);
  const [project, setProject] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser || !projectId) return;
      
      try {
        // Fetch project details
        const projectData = await getProjectById(projectId);
        setProject(projectData);
        
        // Verify project belongs to current company
        if (projectData.companyId !== currentUser.uid) {
          setError("You don't have permission to view these applications");
          setLoading(false);
          return;
        }
        
        // Fetch applications for this project - SIMPLE QUERY
        const applications = await getProjectApplications(projectId);
        
        // Fetch student profiles for each application
        const applicationsWithProfiles = await Promise.all(
          applications.map(async (app) => {
            try {
              const studentDoc = await getDoc(doc(db, 'studentProfiles', app.studentId));
              if (studentDoc.exists()) {
                const studentData = studentDoc.data();
                return {
                  ...app,
                  studentProfile: {
                    fullName: studentData.fullName || `${studentData.firstName || ''} ${studentData.lastName || ''}`.trim() || 'Student',
                    photoURL: studentData.photoURL || '',
                    major: studentData.major || 'Not specified',
                    college: studentData.college || studentData.university || 'Not specified',
                    email: studentData.email || ''
                  }
                };
              }
              return app;
            } catch (error) {
              console.error('Error fetching student profile:', error);
              return app;
            }
          })
        );
        
        // Filter out withdrawn applications and sort by date (newest first)
        const filteredApplications = applicationsWithProfiles
          .filter(app => app.status !== 'withdrawn')
          .sort((a, b) => {
            const aTime = a.appliedAt?.seconds || a.appliedAt?.toDate?.() || 0;
            const bTime = b.appliedAt?.seconds || b.appliedAt?.toDate?.() || 0;
            return bTime - aTime;
          });
        
        setApplications(filteredApplications);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching applications:', err);
        setError('Failed to load applications: ' + err.message);
        setLoading(false);
      }
    };
    
    fetchData();
  }, [currentUser, projectId]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Recently';
    
    try {
      let date;
      if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp.toDate) {
        date = timestamp.toDate();
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else {
        date = new Date(timestamp);
      }
      
      if (isNaN(date.getTime())) return 'Recently';
      
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Recently';
    }
  };

  const handleViewApplication = (application) => {
    setSelectedApplication(application);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedApplication(null);
  };

  const handleApplicationUpdate = () => {
    // Refresh applications data after update
    window.location.reload();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return { bg: 'rgba(251, 191, 36, 0.1)', color: 'var(--warning)' };
      case 'accepted':
        return { bg: 'rgba(34, 197, 94, 0.1)', color: 'var(--success)' };
      case 'rejected':
        return { bg: 'rgba(239, 68, 68, 0.1)', color: 'var(--danger)' };
      default:
        return { bg: 'rgba(156, 163, 175, 0.1)', color: '#6b7280' };
    }
  };

  return (
    <>
      <Navigation />
      <div className="container" style={{ padding: '60px 0' }}>
        <div className="section-header">
          <h2>{project?.title || 'Project'} Applications</h2>
          <p>{applications.length} {applications.length === 1 ? 'candidate has' : 'candidates have'} applied to this project</p>
          <Link 
            to="/company/dashboard"
            className="btn btn-outline"
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              marginBottom: '30px',
              display: 'inline-block'
            }}
          >
            ← Back to Dashboard
          </Link>
        </div>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '18px', color: '#666' }}>Loading applications...</div>
          </div>
        ) : error ? (
          <div style={{ 
            color: 'var(--danger)', 
            padding: '20px', 
            backgroundColor: 'rgba(239, 68, 68, 0.1)', 
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        ) : (
          <div>
            {applications.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '60px 20px'
              }}>
                <div style={{ 
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  fontSize: '32px'
                }}>
                  ��
                </div>
                <h3 style={{ marginBottom: '12px' }}>No applications yet</h3>
                <p style={{ color: '#666', marginBottom: '20px' }}>
                  When students apply to this project, their applications will appear here.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {applications.map(app => {
                  const statusStyle = getStatusColor(app.status);
                  
                  return (
                    <div key={app.id} style={{ 
                      padding: '30px', 
                      border: '1px solid #e5e7eb',
                      borderRadius: '16px',
                      backgroundColor: 'white',
                      transition: 'all 0.2s ease'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                          <div style={{
                            width: '60px',
                            height: '60px',
                            borderRadius: '50%',
                            overflow: 'hidden',
                            background: app.studentProfile?.photoURL ? 'none' : 'var(--primary)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '24px',
                            fontWeight: 'bold',
                            color: app.studentProfile?.photoURL ? 'transparent' : 'white',
                            flexShrink: 0
                          }}>
                            {app.studentProfile?.photoURL ? (
                              <img 
                                src={app.studentProfile.photoURL} 
                                alt={app.studentProfile?.fullName || 'Student'} 
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              (app.studentProfile?.fullName || app.studentName || 'S').charAt(0).toUpperCase()
                            )}
                          </div>
                          
                          <div>
                            <h4 style={{ 
                              marginBottom: '5px', 
                              fontSize: '18px', 
                              fontWeight: 'bold',
                              color: '#333'
                            }}>
                              {app.studentProfile?.fullName || app.studentName || 'Student'}
                            </h4>
                            <p style={{ 
                              color: '#6b7280', 
                              fontSize: '14px',
                              margin: 0
                            }}>
                              {app.studentProfile?.college || 'University'} • {app.studentProfile?.major || 'Major'}
                            </p>
                          </div>
                        </div>
                        
                        <span style={{
                          background: statusStyle.bg,
                          color: statusStyle.color,
                          padding: '6px 12px',
                          borderRadius: '50px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          textTransform: 'capitalize'
                        }}>
                          {app.status}
                        </span>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                        <p style={{ 
                          color: '#666', 
                          fontSize: '14px',
                          margin: 0
                        }}>
                          Applied {formatDate(app.appliedAt)}
                        </p>
                        
                        {app.videoUrl && (
                          <button 
                            onClick={() => window.open(app.videoUrl, '_blank')}
                            className="btn btn-outline"
                            style={{
                              padding: '6px 12px',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            title="Watch Video Introduction"
                          >
                            Video
                          </button>
                        )}
                      </div>
                      
                      <button 
                        onClick={() => handleViewApplication(app)}
                        className="btn btn-primary"
                        style={{
                          padding: '12px 24px',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          borderRadius: '8px',
                          width: 'auto'
                        }}
                      >
                        View Application
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      
      {selectedApplication && (
        <ApplicationModal
          application={selectedApplication}
          isOpen={showModal}
          onClose={handleCloseModal}
          onUpdate={handleApplicationUpdate}
        />
      )}
    </>
  );
};

export default ProjectApplications;